import {
  Calendar,
  Comment,
  EmoCheck,
  EmoEyes,
  EmoMedal,
  EmoRacha,
  EmoRachaActive,
  Heart,
  HeartFilled,
  Market,
  Medal,
  Pin,
  Progress,
  Star,
  StarFilled,
  User,
  UserFilled,
} from "@assets"
import { Head, Link, router } from "@inertiajs/react"
import { BadgeCheck, Clock, Eye, Trophy } from "lucide-react"
import { useEffect } from "react"

import { ContentActionsMenu } from "@/components/content-actions-menu"
import { ReferralCard } from "@/components/referral-card"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Card, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import AppLayout from "@/layouts/app-layout"
import { chatGroupsPath, settingsProfilePath } from "@/routes"

interface UserProfile {
  id: number
  name: string
  username?: string | null
  bio?: string
  location?: string
  avatar_url?: string
  joined_date: string
  verified: boolean
  origin_city?: string | null
  current_city?: { id: number; name: string } | null
  neighborhood?: { id: number; name: string } | null
  points?: number
  level?: string | null
  badges_count?: number
  stats: {
    reputation: number
    events_attended: number
    forum_posts: number
    marketplace_items: number
    raffles_won: number
    reviews_count: number
    average_rating: number
    streak_days: number
  }
  profile_completion_percentage?: number
  referral_code?: string
  referrals_count?: number
  interests: string[]
  badges: {
    id: number
    name: string
    description: string
    category?: string | null
    icon_url?: string | null
    earned_date: string
  }[]
}

interface Activity {
  id: number
  type:
    | "forum_post"
    | "event_attendance"
    | "marketplace_sale"
    | "review"
    | "raffle_win"
  title: string
  description?: string
  date: string
  url?: string
}

interface Review {
  id: number
  rating: number
  comment: string
  created_at: string
  reviewer: {
    id: number
    name: string
    avatar_url?: string
  }
  context: string
}

interface Props {
  user: UserProfile
  recent_activities: Activity[]
  reviews: Review[]
  has_business: boolean
  is_own_profile: boolean
}

export default function UserShow({
  user,
  recent_activities,
  reviews,
  has_business,
  is_own_profile,
}: Props) {
  // Scroll to sharing section when navigating from challenge
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get("scrollToSharing") === "true") {
      setTimeout(() => {
        const sharingSection = document.getElementById("sharing-section")
        if (sharingSection) {
          sharingSection.scrollIntoView({ behavior: "smooth", block: "center" })
        }
      }, 300)
    }
  }, [])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("es-ES", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  }
  const profileCompletion = Math.max(
    0,
    Math.min(100, user.profile_completion_percentage ?? 0),
  )
  const userLevel = user.level ?? "Vecino Activo"
  const badgesCount = user.badges_count ?? user.badges.length

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "forum_post":
        return <Comment className="h-5 w-5" />
      case "event_attendance":
        return <Calendar className="h-4.5 w-4.5" />
      case "marketplace_sale":
        return <Market className="h-5 w-5" />
      case "review":
        return <Star className="h-5 w-5" />
      case "raffle_win":
        return <Trophy className="h-5 w-5" />
      default:
        return <User className="h-5 w-5" />
    }
  }

  const handleContact = () => {
    router.post(
      chatGroupsPath(),
      { user_id: user.id },
      {
        preserveScroll: true,
      },
    )
  }

  return (
    <AppLayout>
      <Head title={`${user.name} - Perfil`} />

      <div className="mx-auto max-w-4xl space-y-6 pt-4">
        <div className="relative flex flex-col items-center gap-[9px] px-4 md:space-y-0 md:space-x-6">
          <div className="absolute top-0 right-4">
            <ContentActionsMenu
              contentType="User"
              contentId={user.id}
              authorId={user.id}
              authorName={user.name}
            />
          </div>
          <Avatar className="h-[102px] w-[102px] border-2 border-white">
            <AvatarImage src={user.avatar_url} />
            <AvatarFallback className="bg-modal p-1">
              <UserFilled className="fill-chart-2 h-full w-full" />
            </AvatarFallback>
          </Avatar>
          <h1 className="text-chart-5 -mt-[3px] h-[33px] text-[24px] font-semibold">
            {user.name}
          </h1>
          {(user.username ?? user.verified) && (
            <div className="flex items-center">
              {user.username && (
                <span className="text-muted-foreground -mt-[2px] h-[20px] text-[13px] tracking-[0.2px]">
                  @{user.username}
                </span>
              )}
              {user.verified && (
                <span className="bg-primary ml-1 flex h-[19px] items-center rounded-[8px]">
                  <BadgeCheck className="text-primary mr-1 h-4.5 w-4.5 fill-white" />
                  <span className="mr-2 h-[13px] text-[9.5px] font-semibold tracking-[0.57px] text-white">
                    {" "}
                    VERIFICADO
                  </span>
                </span>
              )}
            </div>
          )}
          {user.bio && (
            <p className="text-chart-5 mt-[14px] mb-[10px] text-center text-[14px] leading-[24px] tracking-[0.21px] text-balance">
              {user.bio}
            </p>
          )}
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-center sm:gap-4">
            {(user.location ?? user.origin_city) && (
              <div className="text-muted-foreground flex flex-wrap items-center gap-1 text-[13px] leading-[20px] tracking-[0.2px]">
                {user.location && (
                  <div className="flex items-center gap-[8px]">
                    <Pin className="h-4 w-4" />
                    <span>
                      {user.location}
                      {user.neighborhood ? `, ${user.neighborhood.name}` : ""}
                    </span>
                  </div>
                )}
                {user.origin_city && (
                  <div className="flex items-center space-x-1">
                    <span>•</span>
                    <span>de {user.origin_city}</span>
                  </div>
                )}
              </div>
            )}
            <div className="text-muted-foreground flex items-center gap-[9px] text-[13px] leading-[20px] tracking-[0.2px]">
              <Calendar className="h-4 w-4" />
              <span>Miembro desde {formatDate(user.joined_date)}</span>
            </div>
            <div className="text-chart-5 my-[9.5px] flex items-center text-[14px] leading-[21px] tracking-[0.14px]">
              <span className="bg-card mr-[9px] h-[32px] w-[32px] items-center rounded-full p-[4px]">
                <StarFilled className="fill-star h-full w-full" />
              </span>
              <span className="font-semibold">{user.stats.reputation}</span>
              &nbsp;puntos de reputación
            </div>
          </div>
          <div className="flex flex-col space-y-2">
            {!is_own_profile && (
              <Button
                className="h-[39px] w-[177px] items-center rounded-[14px] py-[9px] text-[15px] leading-[23px] font-normal tracking-[0.45px]"
                onClick={handleContact}
              >
                <Comment className="size-6 fill-white" />
                Mensaje
              </Button>
            )}
            {is_own_profile && (
              <Link
                className="bg-primary h-[39px] w-[177px] items-center rounded-[14px] py-[9px] text-center text-[15px] leading-[23px] font-normal tracking-[0.45px] text-white"
                href={settingsProfilePath()}
              >
                Editar perfil
              </Link>
            )}
          </div>
        </div>

        {/* Progreso de perfil */}
        <div className="space-y-[2px]">
          <div className="bg-card flex w-full items-center px-4 pt-[11px] pb-[16px]">
            <span className="bg-modal mr-[13px] h-[40px] w-[40px] items-center rounded-full">
              <Progress
                className={`h-full w-auto ${profileCompletion === 100 ? "fill-active" : "fill-star"}`}
              />
            </span>

            <div className="flex w-full flex-col">
              <span className="text-chart-5 h-[22px] text-[14px] font-semibold">
                Progreso de perfil
              </span>

              <div className="bg-modal h-[10px] w-full overflow-hidden rounded-full">
                <div
                  className={`h-full rounded-full transition-all ${profileCompletion === 100 ? "bg-active" : "bg-star"}`}
                  style={{ width: `${profileCompletion}%` }}
                />
              </div>
            </div>
            <span
              className={`ml-[9px] h-[29px] w-[61px] text-[21px] font-bold ${profileCompletion === 100 ? "text-active" : "text-star"}`}
            >
              {profileCompletion}%
            </span>
          </div>

          {/* Indicadores: Racha / Puntos / Nivel */}
          <div className="grid grid-cols-2 gap-[2px]">
            <div className="bg-card flex flex-col items-center p-4 text-center">
              <div
                className={`h-[40px] w-[40px] p-0.5 ${user.stats.streak_days > 0 ? "bg-modal rounded-full" : ""}`}
              >
                <img
                  src={user.stats.streak_days > 0 ? EmoRachaActive : EmoRacha}
                  className="h-full w-full"
                />
              </div>
              <div className="text-primary h-[24px] text-[17px] font-bold tracking-[0.42px]">
                {user.stats.streak_days} Días
              </div>
              <div className="text-chart-5 h-[12px] text-[9px] font-bold tracking-[0.27px]">
                RACHA
              </div>
            </div>
            <div className="bg-card flex flex-col items-center p-4 text-center">
              <div
                className={`bg-modal h-[40px] w-[40px] rounded-full p-0.5 text-[30px]`}
              >
                <img src={EmoMedal} alt="" />
              </div>
              <div className="text-primary h-[24px] text-[17px] font-bold tracking-[0.42px]">
                {userLevel}
              </div>
              <div className="text-chart-5 h-[12px] text-[9px] font-bold tracking-[0.27px]">
                NIVEL
              </div>
            </div>
          </div>
        </div>
        {/* Stats Grid */}
        <div className="border-chart-4 grid grid-cols-2 gap-4 border-y px-[17px] pt-[20px] pb-[16px] md:grid-cols-4">
          <div className="bg-card flex flex-col items-center rounded-[14px] p-4 text-center">
            <div className="text-primary h-[39px] text-[28px] font-bold">
              {user.stats.events_attended}
            </div>
            <div className="text-chart-5 h-[12px] text-[9px] font-bold tracking-[0.27px]">
              EVENTOS ASISTIDOS
            </div>
          </div>
          <div className="bg-card flex flex-col items-center rounded-[14px] p-4 text-center">
            <div className="text-primary h-[39px] text-[28px] font-bold">
              {user.stats.forum_posts}
            </div>
            <div className="text-chart-5 h-[12px] text-[9px] font-bold tracking-[0.27px]">
              POSTS
            </div>
          </div>
          <div className="bg-card flex flex-col items-center rounded-[14px] p-4 text-center">
            <div className="text-primary h-[39px] text-[28px] font-bold">
              {user.stats.raffles_won}
            </div>
            <div className="text-chart-5 h-[12px] text-[9px] font-bold tracking-[0.27px]">
              SORTEOS
            </div>
          </div>
          <div className="bg-card flex flex-col items-center rounded-[14px] p-4 text-center">
            <div className="text-primary h-[39px] text-[28px] font-bold">
              {user.stats.reviews_count}
            </div>
            <div className="text-chart-5 h-[12px] text-[9px] font-bold tracking-[0.27px]">
              RESEÑAS
            </div>
          </div>
        </div>

        {/* Referral Card - Solo visible para el propio usuario */}
        {is_own_profile && user.referral_code && (
          <div id="sharing-section">
            <ReferralCard
              referralCode={user.referral_code}
              referralsCount={user.referrals_count ?? 0}
            />
          </div>
        )}

        {/* Tabs Content */}
        <Tabs defaultValue="activity" className="space-y-6">
          <div className="flex justify-center">
            <TabsList>
              <TabsTrigger value="activity">Actividad</TabsTrigger>
              <TabsTrigger value="badges">Insignias</TabsTrigger>
              {has_business && (
                <TabsTrigger value="reviews">Reseñas</TabsTrigger>
              )}
              <TabsTrigger value="interests">Intereses</TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="activity" className="space-y-4 px-4">
            <Card className="gap-[22px] border-0">
              <CardTitle className="text-muted-foreground flex items-center justify-center gap-[9px] px-4 text-[14px] leading-[21px] font-medium tracking-[0.28px]">
                <div
                  className={`bg-modal h-[44px] w-[44px] rounded-full p-0.5`}
                >
                  <img src={EmoCheck} className="h-full w-full" />
                </div>
                Actividad reciente
              </CardTitle>
              {recent_activities.length > 0 ? (
                <div className="px-[20px] pb-[20px]">
                  {recent_activities.map((activity) => (
                    <div
                      key={activity.id}
                      className="border-chart-4 flex items-start gap-[13px] border-b px-[7px] py-[12px]"
                    >
                      {getActivityIcon(activity.type)}
                      <div className="text-chart-5 flex-1 text-[13px] leading-[17px] tracking-[0.2px]">
                        {activity.url ? (
                          <Link href={activity.url} className="hover:underline">
                            {activity.title}
                          </Link>
                        ) : (
                          activity.title
                        )}
                        {activity.description && (
                          <p className="">{activity.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-muted-foreground py-8 text-center">
                  <Clock className="mx-auto mb-2 h-8 w-8" />
                  <p>No hay actividad reciente</p>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="badges" className="space-y-4 px-4">
            <Card className="gap-[22px] border-0">
              <CardTitle className="text-muted-foreground flex items-center justify-center gap-[9px] px-4 text-[14px] leading-[21px] font-medium tracking-[0.28px]">
                <div
                  className={`bg-modal h-[44px] w-[44px] items-center rounded-full p-0.5 text-center text-[35px]`}
                >
                  <img src={EmoMedal} className="h-full w-full" />
                </div>
                Insignias
                <span className="bg-primary items-center rounded-[7px] px-4 text-center text-[21px] leading-[31px] text-white">
                  {badgesCount}
                </span>
              </CardTitle>
              {user.badges.length > 0 ? (
                <div className="px-[20px] pb-[20px]">
                  {user.badges.map((badge) => (
                    <div
                      key={badge.id}
                      className="border-chart-4 flex items-start gap-[8px] border-b px-[7px] py-[12px]"
                    >
                      <Medal className="fill-muted-foreground h-6 w-auto" />
                      <div className="text-chart-5 text-[14px] leading-[21px]">
                        <h4 className="font-semibold">{badge.name}</h4>
                        <p className="text-chart-5 text-[13px] leading-[17px] tracking-[0.2px]">
                          {badge.description}
                        </p>
                        <div className="text-chart-1 text-[12px] leading-[18px] tracking-[0.18px]">
                          {formatDate(badge.earned_date)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-muted-foreground py-8 text-center">
                  <Medal className="mx-auto mb-2 h-8 w-8" />
                  <p>Aún no se han conseguido insignias</p>
                </div>
              )}
            </Card>
          </TabsContent>

          {has_business && (
            <TabsContent value="reviews" className="space-y-4 px-4">
              <Card className="gap-[22px] border-0">
                <CardTitle className="text-muted-foreground flex items-center justify-center gap-[9px] px-4 text-[14px] leading-[21px] font-medium tracking-[0.28px]">
                  <div
                    className={`bg-modal h-[44px] w-[44px] items-center rounded-full p-1 text-center text-[35px]`}
                  >
                    <StarFilled className="fill-star h-full w-full" />
                  </div>
                  Reseñas recibidas
                  <span className="bg-primary items-center rounded-[7px] px-4 text-center text-[21px] leading-[31px] text-white">
                    {reviews.length}
                  </span>
                  {/* {reviews.length > 0 && (
                    <div className="flex items-center space-x-2">
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      <span className="font-medium">{user.stats.average_rating.toFixed(1)}</span>
                    </div>
                  )} */}
                </CardTitle>
                {reviews.length > 0 ? (
                  <div className="px-[20px] pb-[20px]">
                    {reviews.map((review) => (
                      <div
                        key={review.id}
                        className="border-chart-4 flex items-start gap-[13px] border-b px-[7px] py-[12px]"
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={review.reviewer.avatar_url} />
                          <AvatarFallback>
                            <UserFilled className="h-4 w-4 fill-white" />
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="mb-2 flex flex-col items-start space-x-2">
                            <span className="font-medium">
                              {review.reviewer.name}
                            </span>
                            <div className="flex items-center">
                              {Array.from({ length: 5 }).map((_, i) => (
                                <StarFilled
                                  key={i}
                                  className={`size-4 ${
                                    i < review.rating
                                      ? "fill-star"
                                      : "fill-chart-2"
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                          <div
                            className="text-sm"
                            dangerouslySetInnerHTML={{ __html: review.comment }}
                          />
                          <div className="text-muted-foreground mt-1 text-xs">
                            {formatDate(review.created_at)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-muted-foreground py-8 text-center">
                    <Eye className="mx-auto mb-2 h-8 w-8" />
                    <p>Aún no hay reseñas</p>
                  </div>
                )}
              </Card>
            </TabsContent>
          )}

          <TabsContent value="interests" className="space-y-4 px-4">
            <Card className="mb-4 gap-[22px] border-0">
              <CardTitle className="text-muted-foreground flex items-center justify-center gap-[9px] px-4 text-[14px] leading-[21px] font-medium tracking-[0.28px]">
                <div
                  className={`bg-modal h-[44px] w-[44px] items-center rounded-full p-0.5 text-center text-[35px]`}
                >
                  <img
                    src={EmoEyes}
                    alt="eyes"
                    className="mr-1 h-full w-auto"
                  />
                </div>
                Intereses
              </CardTitle>
              {user.interests.length > 0 ? (
                <div className="border-chart-2 mx-4 flex flex-wrap items-center justify-center gap-[10px] border-b px-[20px] pb-[40px]">
                  {user.interests.map((interest, index) => (
                    <span
                      key={index}
                      className="bg-modal text-chart-5 flex items-center rounded-[8px] py-[3px] pr-[7px] pl-[3px] text-[9.5px] leading-[14px] font-bold tracking-[0.57px]"
                    >
                      <HeartFilled className="text-destructive mr-1 h-[18px] w-auto" />
                      {interest.toUpperCase()}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="text-muted-foreground py-8 text-center">
                  <Heart className="mx-auto mb-2 h-8 w-8" />
                  <p>No se han definido intereses</p>
                  {is_own_profile && (
                    <Link href={settingsProfilePath()}>
                      <Button variant="outline" size="sm" className="mt-2">
                        Añadir intereses
                      </Button>
                    </Link>
                  )}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  )
}
